import React, { useMemo, useState } from "react";
import { Box, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { usePlatform } from "../platform/store";
import {
  InlineActions,
  PageRoot,
  PrimaryAction,
  SectionCard,
  SummaryGrid,
} from "./ui/PlatformPrimitives";

const documentTypes = [
  "IFRA Certificate",
  "SDS",
  "Allergen Declaration",
  "GMP Statement",
  "CPSR",
  "Claims Substantiation",
];

const DocumentSubmission = () => {
  const theme = useTheme();
  const { products, vendors, addDocuments, documents } = usePlatform();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [type, setType] = useState("IFRA Certificate");
  const [productId, setProductId] = useState(products[0]?.id || "");
  const [vendorId, setVendorId] = useState(vendors[0]?.id || "");
  const [errorMessage, setErrorMessage] = useState("");

  const selectedProduct = products.find((product) => product.id === productId);
  const outstanding = useMemo(
    () => selectedProduct?.requiredDocuments?.filter(
      (requiredType) => !documents.some((document) => document.productId === productId && document.type === requiredType)
    ) || [],
    [documents, productId, selectedProduct]
  );

  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
  };

  const handleSubmit = async () => {
    try {
      setErrorMessage("");
      await addDocuments({ files: selectedFiles, type, productId, vendorId });
      setSelectedFiles([]);
    } catch (error) {
      setErrorMessage(error.message || "Unable to upload documents.");
    }
  };

  return (
    <PageRoot>
      <SummaryGrid
        items={[
          ["Queued upload", selectedFiles.length],
          ["Required docs", selectedProduct?.requiredDocuments?.length || 0],
          ["Outstanding", outstanding.length],
        ]}
      />

      {errorMessage ? <Typography color="error">{errorMessage}</Typography> : null}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, xl: 7 }}>
          <SectionCard
            eyebrow="Intake"
            title="Document upload"
            action={
              <InlineActions>
                <PrimaryAction onClick={handleSubmit} disabled={!selectedFiles.length}>
                  Add to repository
                </PrimaryAction>
              </InlineActions>
            }
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth select label="Type" value={type} onChange={(event) => setType(event.target.value)}>
                  {documentTypes.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth select label="Product" value={productId} onChange={(event) => setProductId(event.target.value)}>
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth select label="Vendor" value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Box
              component="label"
              sx={{
                mt: 3,
                display: "grid",
                gap: 1,
                p: 4,
                // Glass-inset drop zone: dashed rim sits on the page gradient
                // and brightens on hover the same way iOS sheet wells do.
                // Concentric inset radius (12) inside the 16-radius section.
                borderRadius: "12px",
                border: "1px dashed rgba(0,0,0,0.10)",
                background: theme.glass.inset.background,
                backdropFilter: theme.glass.inset.backdropFilter,
                WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
                cursor: "pointer",
                transition: `background ${theme.motion.tint}, border-color ${theme.motion.tint}`,
                textAlign: "center",
                placeItems: "center",
                "&:hover": {
                  borderColor: "rgba(0,0,0,0.18)",
                  background: "rgba(255,255,255,0.30)",
                },
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  fontSize: 28,
                  lineHeight: 1,
                  color: "rgba(0,0,0,0.60)",
                }}
              >
                {"⬆"}
              </Box>
              <Typography variant="h4">Drop files or browse</Typography>
              <Typography variant="caption" color="text.secondary">
                Files are stored as repository records and tied to the selected product and vendor.
              </Typography>
              <input type="file" hidden multiple onChange={handleFileChange} />
            </Box>

            <Box sx={{ display: "grid", gap: 1, mt: 3 }}>
              {selectedFiles.map((file) => (
                <Paper
                  key={`${file.name}-${file.size}`}
                  sx={{ p: 2, borderRadius: "12px", display: "flex", justifyContent: "space-between", gap: 2 }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{Math.round(file.size / 1024)} KB</Typography>
                </Paper>
              ))}
            </Box>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, xl: 5 }}>
          <SectionCard eyebrow="Checklist" title={selectedProduct?.name || "Requirements"}>
            <Box sx={{ display: "grid", gap: 1 }}>
              {(selectedProduct?.requiredDocuments || []).map((requiredType) => {
                const present = documents.some(
                  (document) => document.productId === productId && document.type === requiredType
                );
                return (
                  <Paper key={requiredType} sx={{ p: 2, borderRadius: "12px" }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{requiredType}</Typography>
                    <Typography variant="caption" color={present ? "success.main" : "warning.main"} sx={{ fontWeight: 500 }}>
                      {present ? "In repository" : "Missing"}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </PageRoot>
  );
};

export default DocumentSubmission;
